import ErrorIcon from '@atlaskit/icon/core/migration/error';
import { FormatOptionLabelMeta } from '@atlaskit/select';
import { token } from '@atlaskit/tokens';
import { AsyncSelect } from '@databases/atlaskit/Select';
import { useFlaggingCallback } from '@databases/banners';
import { defaultStoreUtils } from '@databases/redux';
import {
  BasicDatabase,
  BrokenDatabase,
  Database,
  ShallowDatabase,
} from '@databases/types/database';
import {
  getDatabaseByContentId,
  searchDatabases,
} from '@databases/utils/database/service';
import { loadDatabaseByContentId } from '@databases/utils/redux/databases/actions';
import React, { FunctionComponent, useState } from 'react';
import { useIntl } from 'react-intl-next';

import { getDatabaseIdFromString } from '../databaseDetect';
import { messages } from '../messages';

import DatabasePickerOption from './DatabasePickerOption';
import { ErrorMessage } from './databasePicker.styles';

type Option = ShallowDatabase | BasicDatabase | BrokenDatabase;

export interface DatabasePickerProps {
  selectedDatabase: Database | BrokenDatabase | undefined;
  onChange: (value: Database | BrokenDatabase | undefined) => void;
  isInvalid?: boolean;
}

export const formatOptionLabel = (
  value: Option,
  meta: FormatOptionLabelMeta<Option>
): React.ReactNode => {
  if (meta.context === 'menu') {
    return <DatabasePickerOption value={value} />;
  } else {
    return value.name;
  }
};

const DatabasePicker: FunctionComponent<DatabasePickerProps> = ({
  selectedDatabase,
  onChange,
  isInvalid,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = defaultStoreUtils.useDispatch();

  const intl = useIntl();
  const { formatMessage } = intl;

  const loadOptions = useFlaggingCallback(
    async (
      inputValue: string
    ): Promise<{ label: string; options: BasicDatabase[] }[] | BasicDatabase[]> => {
      const databaseContentId = getDatabaseIdFromString(inputValue);
      if (databaseContentId) {
        const database = await getDatabaseByContentId(databaseContentId);

        return [database];
      } else {
        const databases: BasicDatabase[] = await searchDatabases(inputValue);

        return [
          {
            label: formatMessage(messages['generic-database-picker.menu-header']),
            options: databases,
          },
        ];
      }
    },
    [formatMessage]
  );

  const handleChange = useFlaggingCallback(
    async (value: Option | null) => {
      if (!value) {
        onChange(undefined);
        return;
      }

      const initialDb = value as ShallowDatabase;
      setIsLoading(true);
      const finalDb = await dispatch(loadDatabaseByContentId(initialDb.contentId, intl));
      onChange(finalDb);

      setIsLoading(false);
    },
    [dispatch, onChange, intl]
  );

  const getOptionValue = (value: Option) => {
    return 'contentId' in value ? value.contentId : '';
  };

  return (
    <>
      <AsyncSelect<Option>
        inputId="database-picker-input"
        value={selectedDatabase ?? null}
        loadOptions={loadOptions}
        formatOptionLabel={formatOptionLabel}
        getOptionValue={getOptionValue}
        onChange={handleChange}
        isLoading={isLoading}
        isClearable={true}
        isInvalid={isInvalid}
        classNamePrefix="database-picker"
        placeholder={formatMessage(messages['generic-database-picker.placeholder'])}
      />
      {isInvalid && (
        <ErrorMessage>
          <ErrorIcon
            LEGACY_size="small"
            label={formatMessage(messages['generic-database-picker.error-label'])}
            color={token('color.text.danger', '#AE2A19')}
          />
          {formatMessage(messages['generic-database-picker.error-message'])}
        </ErrorMessage>
      )}
    </>
  );
};

export default DatabasePicker;
